import { faClose, faCamera, faUpload, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Drawer, Form, Select, InputNumber, Input, DatePicker, message } from "antd";
import Spinner from "@src/components/spinner";
import { useForm } from "antd/es/form/Form";
import { getSitesQuery } from "../../util/query/site.query";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState, useRef } from "react";
import { isEqual, debounce } from 'lodash';
import { CaretDownFilled } from "@ant-design/icons";
import { getAllKeyGroupsQuery, getKeyByIdQuery } from "../../util/query/keyRegister.query";
import { newKeyMutation, updateKeyMutation } from "../../util/mutation/keyRegister.mutation";
import { genericFileUploadSignatureMutation } from '@src/util/mutation/genericFileUpload.mutation';
import dayjs from "dayjs";
import axios from "axios";



const KEY_TYPE_LABELS = {
    metal: "Metal",
    fob: "Fob",
    card: "Card",
    accessCode: "Access Code",
    remote: "Remote",
    padlock: "Padlock",
    masterKey: "Master Key",
    keypad: "Keypad",
};

const MAX_IMAGES = 5;
const MAX_FILE_SIZE_MB = 5;

export const NewKeyModal = ({ visible, close, keyToBeEdit, prefillSiteId = null, prefillSiteName = null }) => {
    const [form] = Form.useForm();
    const [selectedKeyTypes, setSelectedKeyTypes] = useState([]);
    const [keyCounts, setKeyCounts] = useState({});
    const [images, setImages] = useState([]);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const [newKey, {loading: newKeyLoading}] = useMutation(newKeyMutation);
    const [updateKey, {loading: updateKeyLoading}] = useMutation(updateKeyMutation);
    // newKeyLoading/updateKeyLoading only cover the final GraphQL call — the image
    // uploads that run before it (often the slowest part) aren't reflected there,
    // so the button re-enables while a submit is still genuinely in flight. Track
    // the whole handleSubmit body explicitly instead.
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [getSites, { data: siteData, loading: siteLoading }] = useLazyQuery(getSitesQuery);
    const [getAllKeyGroups, { data: allKeyGroups, loading: allKeyGroupLoading }] = useLazyQuery(getAllKeyGroupsQuery);

    // The list row backing `keyToBeEdit` never carries `images` (getAllKeysQuery doesn't
    // select it, and even the raw storage keys wouldn't be viewable without signing) —
    // fetch the fully resolved key so existing photos can be shown, and so the original
    // storage key can be sent back unchanged on save instead of being dropped.
    const { data: keyDetailData } = useQuery(getKeyByIdQuery, {
        variables: { keyId: keyToBeEdit?.record?._id },
        skip: !keyToBeEdit?.record?._id || !visible,
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        const existingImages = keyDetailData?.getKeyById?.response?.images;
        if (visible && keyToBeEdit && existingImages?.length) {
            setImages(existingImages.map((img, idx) => ({
                uid: `existing-${idx}`,
                name: img.key?.split('/').pop() || `Image ${idx + 1}`,
                preview: img.url,
                existing: true,
                key: img.key,
            })));
        }
    }, [keyDetailData?.getKeyById?.response?.images, visible, keyToBeEdit]);

    // Pre-populate form when editing
    useEffect(() => {
        if (keyToBeEdit && visible) {
            const record = keyToBeEdit.record;

            const types = record.keyTypes?.map(kt => kt.type) || [];
            const counts = {};
            record.keyTypes?.forEach(kt => {
                counts[kt.type] = kt.count || 1;
            });

            setSelectedKeyTypes(types);
            setKeyCounts(counts);

            const formValues = {
                site: record.siteId,
                keyGroupId: record.keyGroupId || undefined,
                keyTypes: types,
                dateReceived: record.dateReceived ? record.dateReceived.split('T')[0] : undefined,
                keyLocation: record?.keyLocation,
                status: record?.status,
                custodian: record?.custodian
            };

            types.forEach(type => {
                formValues['keyCounts'] = formValues['keyCounts'] || {};
                formValues['keyCounts'][type] = counts[type] || 1;
            });

            record.keyTypes?.forEach(kt => {
                if (kt.type === 'accessCode' && kt.accessCodes?.length) {
                    formValues['accessCodes'] = {};
                    kt.accessCodes.forEach(ac => {
                        formValues['accessCodes'][ac.index] = ac.code;
                    });
                }
            });

            form.setFieldsValue(formValues);

            getSites({
                fetchPolicy: "cache-and-network",
                variables: {
                    input: {
                        filters: null,
                        search: record.site?.sitename || "",
                        page: 1,
                        pageSize: 100,
                    }
                },
            });

            if (record.keygroup?.name) {
                getAllKeyGroups({
                    fetchPolicy: "cache-and-network",
                    variables: {
                        input: {
                            filters: null,
                            search: record.keygroup.name || "",
                            page: 1,
                            pageSize: 5,
                        }
                    },
                });
            }

        } else if (!visible) {
            form.resetFields();
            setSelectedKeyTypes([]);
            setKeyCounts({});
            setImages([]);
        }
    }, [keyToBeEdit, visible]);

    // "Already received it" from the post-site-creation popup (postSiteKeyCheckModal.jsx) - a
    // brand new key (goes through newKey, not updateKey), just pre-pointed at the site that was
    // just created so the creator doesn't have to search for it. Not folded into keyToBeEdit,
    // which would flip this into edit mode and call the wrong mutation.
    useEffect(() => {
        if (!visible || keyToBeEdit || !prefillSiteId) return;
        form.setFieldsValue({ site: prefillSiteId });
        getSites({
            fetchPolicy: "cache-and-network",
            variables: { input: { filters: null, search: prefillSiteName || "", page: 1, pageSize: 5 } },
        });
    }, [visible, prefillSiteId, keyToBeEdit]);

    useEffect(() => {
        return () => {
            images.forEach(img => {
                if (img.preview && !img.existing) {
                    URL.revokeObjectURL(img.preview);
                }
            });
        };
    }, []);

    const generateUnique6Digit = () => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    };

    // ─── Image helpers ───────────────────────────────────────────────
    const validateFile = (file) => {
        if (!file.type.startsWith('image/')) {
            message.error(`"${file.name}" is not an image file`);
            return false;
        }
        if (file.size / 1024 / 1024 >= MAX_FILE_SIZE_MB) {
            message.error(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`);
            return false;
        }
        return true;
    };

    const addImageFiles = (files) => {
        const fileArray = Array.from(files);
        const remaining = MAX_IMAGES - images.length;

        if (remaining <= 0) {
            message.warning(`Maximum ${MAX_IMAGES} images allowed`);
            return;
        }

        if (fileArray.length > remaining) {
            message.info(`Only ${remaining} more image${remaining > 1 ? 's' : ''} can be added. First ${remaining} will be used.`);
        }

        const validFiles = fileArray.slice(0, remaining).filter(validateFile);

        const newImages = validFiles.map((file) => ({
            uid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            name: file.name,
            preview: URL.createObjectURL(file),
            existing: false,
        }));

        if (newImages.length) {
            setImages(prev => [...prev, ...newImages]);
        }
    };

    const removeImage = (uid) => {
        setImages(prev => {
            const img = prev.find(i => i.uid === uid);
            if (img?.preview && !img.existing) {
                URL.revokeObjectURL(img.preview);
            }
            return prev.filter(i => i.uid !== uid);
        });
    };


    const [genericFileUploadSignature, {loading: genericFileUploadSignatureLoading}] = useMutation(genericFileUploadSignatureMutation);
    

    // ─── Submit ──────────────────────────────────────────────────────
    const handleSubmit = async() => {
        setIsSubmitting(true);
        try {
            const values = await form.validateFields();

            const serialNumber = siteData?.sites?.sites?.find((site) => site?._id == values.site)?.sitename.replaceAll(" ", "-") + "-" + generateUnique6Digit();

            const imagePayloads = []
            await Promise.all(
                images.map(async (img) => {

                    // Existing photos weren't touched — send the original storage key back
                    // unchanged instead of fabricating a new (unuploaded) one.
                    if (img.existing) {
                        imagePayloads.push(img.key);
                        return;
                    }

                    const imgName = ("keys/"+values.site?.replaceAll(" ","-")+"/"+serialNumber+"-"+dayjs().valueOf()+"-"+img.name?.replaceAll(" ","-"))?.toLowerCase();

                    const fileUploadUrlsResponse = await genericFileUploadSignature({
                        variables:{
                            input:{
                                fileName : imgName,
                                objectType: "keys"
                            }
                        }
                    });

                    const uploadResponse = fileUploadUrlsResponse?.data?.genericFileUploadSignatureUrl?.response;

                    await axios.put(uploadResponse?.url, img?.file,{
                        headers: {
                            'Content-Type': img?.file?.type ,
                        },
                    });

                    // The server strips whitespace from the filename before uploading (see
                    // genericFileUploadSignatureUrl), so it can differ from the name we asked
                    // for — store the name it actually used, or the image silently 404s later.
                    imagePayloads.push(uploadResponse?.originalFileName || imgName);
                })
            );

            const transformedInput = {
                siteId: values.site,
                keyGroupId: values.keyGroupId,
                keyTypes: selectedKeyTypes.map(type => {
                    const keyTypeData = { type, count: keyCounts[type] || 1 };
                    if (type === 'accessCode') {
                        const accessCodesData = [];
                        for (let i = 0; i < (keyCounts[type] || 1); i++) {
                            const code = values.accessCodes?.[i];
                            if (code) accessCodesData.push({ index: i, code });
                        }
                        keyTypeData.accessCodes = accessCodesData;
                    }
                    return keyTypeData;
                }),
                keySerial: siteData?.sites?.sites?.find((site) => site?._id == values.site)?.sitename.replaceAll(" ", "-") + "-" + generateUnique6Digit(),
                totalKeys: selectedKeyTypes.reduce((sum, type) => sum + (keyCounts[type] || 1), 0),
                dateReceived: dayjs(values.dateReceived).toDate(),
                keyLocation: values.keyLocation || "Office",
                status: values.status || "Live",
                custodianType: values?.custodianType || "In-House",
                images: imagePayloads,
            };

            if (keyToBeEdit) {
                await updateKey({
                    variables: {
                        input: { ...transformedInput },
                        updateKeyId: keyToBeEdit.record._id
                    }
                });
            } else {
                await newKey({ variables: { input: transformedInput } });
            }

            message.success(keyToBeEdit ? 'Key updated successfully' : 'Key created successfully');
            form.resetFields();
            setSelectedKeyTypes([]);
            setKeyCounts({});
            setImages([]);
            close();

        } catch (err) {
            console.error('Submit Error:', err);
            message.error(keyToBeEdit ? err?.message || 'Failed to update key' : err?.message || 'Failed to create key');
        } finally {
            setIsSubmitting(false);
        }
    };

    const debouncedGetSites = useMemo(
        () => debounce((searchTerm) => {
            getSites({
                fetchPolicy: "cache-and-network",
                variables: { input: { filters: null, search: searchTerm, page: 1, pageSize: 5 } },
            });
        }, 300),
        []
    );

    const handleKeyTypeChange = (values) => {
        setSelectedKeyTypes(values);
        setKeyCounts(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(k => {
                if (!values.includes(k)) delete updated[k];
            });
            return updated;
        });
        values.forEach(type => {
            if (!keyCounts[type]) {
                setKeyCounts(prev => ({ ...prev, [type]: 1 }));
            }
        });
    };

    const handleCountChange = (type, count) => {
        setKeyCounts(prev => ({ ...prev, [type]: count }));
        if (type === 'accessCode') {
            const currentCodes = form.getFieldValue('accessCodes') || [];
            form.setFieldValue('accessCodes', currentCodes.slice(0, count));
        }
    };

    const debouncedGetAllKeyGroups = useMemo(
        () => debounce((searchTerm) => {
            getAllKeyGroups({
                fetchPolicy: "cache-and-network",
                variables: { input: { filters: null, search: searchTerm, page: 1, pageSize: 5 } },
            });
        }, 300),
        []
    );

    // ─── Image Section Component ─────────────────────────────────────
    const renderImageSection = () => {
        const hasImages = images.length > 0;
        const canAddMore = images.length < MAX_IMAGES;

        // STATE 1: No images yet — show initial upload area
        if (!hasImages) {
            return (
                <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                        Key Images <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span>
                    </label>
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                            border: '2px dashed #d9d9d9',
                            borderRadius: 8,
                            padding: '28px 20px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            background: '#fafafa',
                            transition: 'border-color 0.2s, background 0.2s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'rgb(13, 92, 99)';
                            e.currentTarget.style.background = 'rgba(13, 92, 99, 0.06)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.background = '#fafafa';
                        }}
                    >
                        <FontAwesomeIcon icon={faUpload} style={{ fontSize: 22, color: '#8c8c8c', marginBottom: 8 }} />
                        <p style={{ margin: 0, fontSize: 14, color: '#595959', fontWeight: 500 }}>
                            Click to upload key images
                        </p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#999' }}>
                            Upload up to {MAX_IMAGES} images (max {MAX_FILE_SIZE_MB}MB each)
                        </p>
                    </div>

                    {/* Camera option below */}
                    {/* <div 
                        onClick={() => cameraInputRef.current?.click()}
                        style={{
                            marginTop: 8,
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontSize: 13,
                            color: '#1677ff',
                            padding: '6px 0',
                        }}
                    >
                        <FontAwesomeIcon icon={faCamera} style={{ marginRight: 6 }} />
                        Or take a photo
                    </div> */}

                    {/* Hidden inputs */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(e) => {
                            if (e.target.files?.length) addImageFiles(e.target.files);
                            e.target.value = '';
                        }}
                        style={{display:'none'}}
                    />
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        hidden
                        style={{display:'none'}}

                        onChange={(e) => {
                            if (e.target.files?.length) addImageFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                </div>
            );
        }

        // STATE 2: Has images — show previews + "Add More" option
        return (
            <div style={{ marginTop: 16, marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                    Key Images <span style={{ color: '#999', fontWeight: 400 }}>({images.length}/{MAX_IMAGES})</span>
                </label>

                {/* Image grid */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {images.map((img) => (
                        <div
                            key={img.uid}
                            style={{
                                position: 'relative',
                                width: 88,
                                height: 88,
                                borderRadius: 8,
                                overflow: 'hidden',
                                border: '1px solid #e8e8e8',
                            }}
                        >
                            <img
                                src={img.preview}
                                alt={img.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                                type="button"
                                onClick={() => removeImage(img.uid)}
                                style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    background: 'rgba(0,0,0,0.55)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                }}
                            >
                                <FontAwesomeIcon icon={faClose} style={{ color: '#fff', fontSize: 10 }} />
                            </button>
                        </div>
                    ))}

                    {/* Add More tile — inline with images */}
                    {canAddMore && (
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                width: 88,
                                height: 88,
                                borderRadius: 8,
                                border: '2px dashed #d9d9d9',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                background: '#fafafa',
                                transition: 'border-color 0.2s, background 0.2s',
                                gap: 4,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'rgb(13, 92, 99)';
                                e.currentTarget.style.background = 'rgba(13, 92, 99, 0.06)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#d9d9d9';
                                e.currentTarget.style.background = '#fafafa';
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} style={{ fontSize: 16, color: '#8c8c8c' }} />
                            <span style={{ fontSize: 11, color: '#8c8c8c' }}>Add More</span>
                        </div>
                    )}
                </div>

                {/* Camera option */}
                {/* {canAddMore && (
                    <div
                        onClick={() => cameraInputRef.current?.click()}
                        style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: '#1677ff',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <FontAwesomeIcon icon={faCamera} />
                        Take a photo
                    </div>
                )} */}

                {/* Hidden inputs */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => {
                        if (e.target.files?.length) addImageFiles(e.target.files);
                        e.target.value = '';
                    }}
                        style={{display:'none'}}

                />
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(e) => {
                        if (e.target.files?.length) addImageFiles(e.target.files);
                        e.target.value = '';
                    }}
                        style={{display:'none'}}

                />
            </div>
        );
    };

    return (
        <Drawer
            title={keyToBeEdit ? "Edit Keyset" : "New Keyset"}
            placement="right"
            closable={true}
            onClose={close}
            closeIcon={<FontAwesomeIcon icon={faClose} className='close-icon' />}
            visible={visible}
            width={600}
            maskClosable={false}
            mask={true}
            footer={
                <div className='drawer-footer'>
                    <button
                        disabled={isSubmitting || newKeyLoading || updateKeyLoading}
                        className={isSubmitting || newKeyLoading || updateKeyLoading ? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'}
                        onClick={handleSubmit}
                    >
                        {isSubmitting || newKeyLoading || updateKeyLoading ? 'Saving...' : keyToBeEdit ? 'Update' : 'Save'}
                    </button>
                    <button disabled={isSubmitting || newKeyLoading || updateKeyLoading} className='drawer-outlined-btn' onClick={close}>
                        Cancel
                    </button>
                </div>
            }
        >
            <Form form={form} requiredMark={false} layout="vertical">

                {/* Site */}
                <Form.Item
                    label={"Site"}
                    name="site"
                    rules={[{ required: true, message: "Please select a site to add new key" }]}
                >
                    <Select
                        showSearch
                        className="custom-select"
                        onSearch={debouncedGetSites}
                        onDropdownVisibleChange={(open) => {
                            if (open) {
                                getSites({
                                    fetchPolicy: "cache-and-network",
                                    variables: { input: { filters: null, search: "", page: 1, pageSize: 5 } },
                                });
                            }
                        }}
                        placeholder="Select site"
                        loading={siteLoading}
                        filterOption={false}
                        suffixIcon={!siteLoading ? <CaretDownFilled /> : <Spinner />}
                    >
                        {siteLoading
                            ? <Select.Option hidden disabled>Loading ...</Select.Option>
                            : siteData?.sites?.sites?.map((site) => (
                                <Select.Option key={site?._id} value={site?._id}>
                                    {site?.sitename}
                                </Select.Option>
                            ))}
                    </Select>
                </Form.Item>

                {/* Key group */}
                <Form.Item
                    label={"Key group"}
                    name="keyGroupId"
                    rules={[{ required: false }]}
                    style={{ marginTop: '16px' }}
                >
                    <Select
                        showSearch
                        className="custom-select"
                        filterOption={false}
                        onSearch={debouncedGetAllKeyGroups}
                        onDropdownVisibleChange={(open) => {
                            if (open) {
                                getAllKeyGroups({
                                    fetchPolicy: "cache-and-network",
                                    variables: { input: { filters: null, search: "", page: 1, pageSize: 5 } },
                                });
                            }
                        }}
                        placeholder="Select Key group"
                        loading={allKeyGroupLoading}
                        suffixIcon={!allKeyGroupLoading ? <CaretDownFilled /> : <Spinner />}
                        allowClear
                    >
                        {allKeyGroupLoading
                            ? <Select.Option hidden disabled>Loading ...</Select.Option>
                            : allKeyGroups?.getKeyGroups?.response?.map((keyGroup) => (
                                <Select.Option key={keyGroup?._id} value={keyGroup?._id}>
                                    {keyGroup?.name}
                                </Select.Option>
                            ))}
                    </Select>
                </Form.Item>

                {/* Key Types */}
                <Form.Item
                    label={"Key Types"}
                    name="keyTypes"
                    rules={[{ required: true, message: "Please select at least one key type" }]}
                    style={{ marginTop: '16px' }}
                >
                    <Select
                        mode="multiple"
                        placeholder="Select key types"
                        onChange={handleKeyTypeChange}
                        className="custom-select"
                        suffixIcon={<CaretDownFilled />}
                    >
                        {Object.entries(KEY_TYPE_LABELS).map(([value, label]) => (
                            <Select.Option key={value} value={value}>{label}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Dynamic per-type quantity + access code inputs */}
                {selectedKeyTypes.map((type) => (
                    <div
                        key={type}
                        style={{
                            background: "#fafafa",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: "12px 16px",
                            marginBottom: 16,
                        }}
                    >
                        <Form.Item
                            label={`Number of ${KEY_TYPE_LABELS[type]} keys`}
                            name={['keyCounts', type]}
                            initialValue={1}
                            rules={[{ required: true, message: `Please enter number of ${KEY_TYPE_LABELS[type]} keys` }]}
                        >
                            <InputNumber
                                min={1}
                                max={20}
                                style={{ width: "100%" }}
                                onChange={(val) => handleCountChange(type, val)}
                            />
                        </Form.Item>

                        {type === 'accessCode' && (keyCounts['accessCode'] >= 1) && (
                            Array.from({ length: keyCounts['accessCode'] || 1 }).map((_, i) => (
                                <Form.Item
                                    key={i}
                                    label={`Access Code ${i + 1}`}
                                    name={['accessCodes', i]}
                                    rules={[{ required: true, message: `Please enter access code ${i + 1}` }]}
                                >
                                    <Input
                                        placeholder={`Enter access code ${i + 1}`}
                                        maxLength={10}
                                    />
                                </Form.Item>
                            ))
                        )}
                    </div>
                ))}


                {/* Date Received */}
                <Form.Item
                    label={"Date Received"}
                    rules={[
                        { required: true, message: "Please enter received date" },
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const selected = new Date(value);
                                const today = new Date();
                                today.setHours(23, 59, 59, 999);
                                if (selected > today) return Promise.reject("Date received cannot be a future date");
                                if (selected.getFullYear() < 2000) return Promise.reject("Date received cannot be before year 2000");
                                return Promise.resolve();
                            }
                        }
                    ]}
                    name="dateReceived"
                    style={{ marginTop: '16px' }}
                >
                    <Input
                        type="date"
                        className="generic-input-control"
                        min="2000-01-01"
                        max={new Date().toISOString().split('T')[0]}
                    />
                </Form.Item>


                {/* ─── Key Images Section ─────────────────────────── */}
                {renderImageSection()}

            </Form>
        </Drawer>
    );
};